import { apiFetch, toSearchParams } from "./api";
import { decodeBase64ToUtf8, decompressFromBase64 } from "./compression";
import { findComponentByType } from "./finder";
import { unbundleSharedSection, normalizeSharedSectionNodeForPage } from "./normalizeSharedSectionNode";
import { applyLegacyPropAliases } from "./legacyPropAliases";
import type {
  ApiSubmitResponse,
  ContactPayload,
  DataInputPayload,
  LoginPayload,
  RegisterPayload,
  RuntimeBootstrapResponse,
  RuntimeSessionResponse,
} from "./contracts";
import type {
  CraftData,
  CraftNodeValue,
  RuntimeSession,
  RuntimeUser,
} from "../runtime/types/core";

export function normalizeSlugFromPathname(pathname: string): string {
  if (!pathname || pathname === "/") return "/";

  // Strip S3 bucket prefix in local dev (e.g. /landing-pages/<eventId>/index.html → /).
  // "landing-pages" is the real backend bucket (R2Service); "landingpages-plus"
  // never existed — kept as alias for old links (audit G9 sibling fix).
  const s3Match = pathname.match(/^\/(?:landing-pages|landingpages-plus)\/[a-f0-9]+(\/.*)$/);
  if (s3Match) {
    const inner = s3Match[1] === "/index.html" ? "/" : s3Match[1]!;
    return inner;
  }

  if (!pathname.startsWith("/")) return `/${pathname}`;
  return pathname;
}

export async function fetchBootstrap(
  slug: string,
): Promise<RuntimeBootstrapResponse> {
  return apiFetch<RuntimeBootstrapResponse>(
    `/bootstrap${toSearchParams({ slug })}`,
  );
}

export async function fetchSession(): Promise<RuntimeSessionResponse> {
  const payload = await apiFetch<unknown>("/session");
  return normalizeRuntimeSession(payload);
}

export async function submitRegister(
  payload: RegisterPayload,
): Promise<ApiSubmitResponse> {
  return apiFetch<ApiSubmitResponse>("/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function submitLogin(
  payload: LoginPayload,
): Promise<ApiSubmitResponse> {
  return apiFetch<ApiSubmitResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function submitLogout(): Promise<ApiSubmitResponse> {
  return apiFetch<ApiSubmitResponse>("/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function submitContact(
  payload: ContactPayload,
): Promise<ApiSubmitResponse> {
  return await apiFetch<ApiSubmitResponse>("/contact", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function submitDataInput(
  payload: DataInputPayload,
): Promise<ApiSubmitResponse> {
  return apiFetch<ApiSubmitResponse>("/data-input/submit", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStringOrUndefined(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  return String(value);
}

function normalizeUserCandidate(value: unknown): RuntimeUser | undefined {
  if (!isRecord(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const userId = asStringOrUndefined(
    raw.userId ?? raw.inviteeId ?? raw.guestId,
  );
  const inviteeId = asStringOrUndefined(
    raw.inviteeId ?? raw.userId ?? raw.guestId,
  );
  const guestId = asStringOrUndefined(
    raw.guestId ?? raw.inviteeId ?? raw.userId,
  );

  return {
    ...raw,
    userId,
    inviteeId,
    guestId,
    mainGuest: asStringOrUndefined(raw.mainGuest),
    state: asStringOrUndefined(raw.state),
    acceptanceState: asStringOrUndefined(raw.acceptanceState),
    additionalFields: isRecord(raw.additionalFields)
      ? raw.additionalFields
      : undefined,
  } as RuntimeUser;
}

function normalizeUserList(value: unknown): RuntimeUser[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const users = value
    .map((item) => normalizeUserCandidate(item))
    .filter((item): item is RuntimeUser => item !== undefined);
  return users.length > 0 ? users : [];
}

export function normalizeRuntimeSession(payload: unknown): RuntimeSession {
  if (!isRecord(payload)) return null;

  const root = payload as Record<string, unknown>;
  const rootAdditionalFields = isRecord(root.additionalFields)
    ? root.additionalFields
    : undefined;

  const userCandidate =
    normalizeUserCandidate(root.user) ??
    normalizeUserCandidate(root.guest) ??
    normalizeUserCandidate(root.invitee) ??
    normalizeUserCandidate(root.profile);
  const companionsCandidate =
    normalizeUserList(userCandidate?.companions) ??
    normalizeUserList(root.companions);

  const mergedUser = {
    ...(userCandidate ?? {}),
    userId:
      (userCandidate?.userId as string | undefined) ??
      (root.userId as string | undefined) ??
      (userCandidate?.inviteeId as string | undefined) ??
      (root.inviteeId as string | undefined) ??
      (userCandidate?.guestId as string | undefined) ??
      (root.guestId as string | undefined),
    inviteeId:
      (userCandidate?.inviteeId as string | undefined) ??
      (root.inviteeId as string | undefined) ??
      (userCandidate?.userId as string | undefined) ??
      (root.userId as string | undefined) ??
      (userCandidate?.guestId as string | undefined) ??
      (root.guestId as string | undefined),
    guestId:
      (userCandidate?.guestId as string | undefined) ??
      (root.guestId as string | undefined) ??
      (userCandidate?.inviteeId as string | undefined) ??
      (root.inviteeId as string | undefined),
    mainGuest:
      (userCandidate?.mainGuest as string | undefined) ??
      (root.mainGuest as string | undefined),
    firstName:
      (userCandidate?.firstName as string | undefined) ??
      (root.firstName as string | undefined),
    lastName:
      (userCandidate?.lastName as string | undefined) ??
      (root.lastName as string | undefined),
    email:
      (userCandidate?.email as string | undefined) ??
      (root.email as string | undefined),
    phone:
      (userCandidate?.phone as string | undefined) ??
      (root.phone as string | undefined),
    mobile:
      (userCandidate?.mobile as string | undefined) ??
      (root.mobile as string | undefined),
    company:
      (userCandidate?.company as string | undefined) ??
      (root.company as string | undefined),
    jobTitle:
      (userCandidate?.jobTitle as string | undefined) ??
      (root.jobTitle as string | undefined),
    title:
      (userCandidate?.title as string | undefined) ??
      (root.title as string | undefined),
    language:
      (userCandidate?.language as string | undefined) ??
      (root.language as string | undefined),
    city:
      (userCandidate?.city as string | undefined) ??
      (root.city as string | undefined),
    country:
      (userCandidate?.country as string | undefined) ??
      (root.country as string | undefined),
    zipCode:
      (userCandidate?.zipCode as string | undefined) ??
      (root.zipCode as string | undefined),
    address:
      (userCandidate?.address as string | undefined) ??
      (root.address as string | undefined),
    address2:
      (userCandidate?.address2 as string | undefined) ??
      (root.address2 as string | undefined),
    username:
      (userCandidate?.username as string | undefined) ??
      (root.username as string | undefined),
    code:
      (userCandidate?.code as string | undefined) ??
      (root.code as string | undefined),
    emailOptIn:
      (userCandidate?.emailOptIn as boolean | undefined) ??
      (root.emailOptIn as boolean | undefined),
    state:
      (userCandidate?.state as string | undefined) ??
      (root.state as string | undefined),
    acceptanceState:
      (userCandidate?.acceptanceState as string | undefined) ??
      (root.acceptanceState as string | undefined),
    additionalFields:
      (isRecord(userCandidate?.additionalFields)
        ? (userCandidate.additionalFields as Record<string, unknown>)
        : undefined) ?? rootAdditionalFields,
    companions: companionsCandidate,
  } as RuntimeUser;

  const session: Record<string, unknown> = {
    ...root,
    user: mergedUser,
  };

  if (session.userId === undefined && mergedUser.userId !== undefined) {
    session.userId = mergedUser.userId;
  }
  if (session.email === undefined && mergedUser.email !== undefined) {
    session.email = mergedUser.email;
  }

  return session as RuntimeSessionResponse;
}

function setStrippedFlag(node: unknown): unknown {
  if (!node || typeof node !== "object") return node;
  const mutable = node as Record<string, unknown>;
  const currentProps =
    mutable.props &&
    typeof mutable.props === "object" &&
    !Array.isArray(mutable.props)
      ? (mutable.props as Record<string, unknown>)
      : {};

  return {
    ...mutable,
    props: {
      ...currentProps,
      stripped: true,
    },
  };
}

function tryParseJson(value: string): unknown | null {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeSerializedPayload(value?: string): string {
  const trimmed = value?.trim();
  if (!trimmed) return "{}";

  const unwrapped = tryParseJson(trimmed);
  if (typeof unwrapped === "string") return unwrapped.trim();

  return trimmed;
}

function normalizeBase64(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const remainder = normalized.length % 4;
  if (remainder === 0) return normalized;
  return `${normalized}${"=".repeat(4 - remainder)}`;
}

async function parseJsonNodeFromCompressed(
  value?: string,
): Promise<CraftNodeValue> {
  const payload = normalizeSerializedPayload(value);

  const directJson = tryParseJson(payload);
  if (directJson !== null) return directJson as CraftNodeValue;

  const normalizedBase64 = normalizeBase64(payload);
  const decodedUtf8 = tryParseJson(decodeBase64ToUtf8(normalizedBase64));
  if (decodedUtf8 !== null) return decodedUtf8 as CraftNodeValue;

  const decompressed = await decompressFromBase64(normalizedBase64);
  const decompressedJson = tryParseJson(decompressed);
  if (decompressedJson !== null) return decompressedJson as CraftNodeValue;

  throw new Error("Failed to parse runtime page payload.");
}

function ensureKnownComponents(
  websiteJson: CraftData,
  knownComponents: Set<string>,
): CraftData {
  const next: CraftData = { ...websiteJson };

  for (const [nodeId, node] of Object.entries(next)) {
    if (typeof node === "string") {
      if (!knownComponents.has(node)) {
        next[nodeId] = {
          type: { resolvedName: "UnknownComponent" },
          props: { missingType: node },
        };
      }
      continue;
    }

    const resolvedName = node.type?.resolvedName;
    if (resolvedName && !knownComponents.has(resolvedName)) {
      next[nodeId] = {
        ...node,
        type: { resolvedName: "UnknownComponent" },
        props: {
          ...(node.props ?? {}),
          missingType: resolvedName,
        },
      };
    }
  }

  return next;
}

export async function buildRenderablePageJson(
  routeSlug: string,
  bootstrap: RuntimeBootstrapResponse,
  knownComponents: Set<string>,
): Promise<string> {
  if (!bootstrap.page) {
    // Defensive: App.tsx already renders a not-found state when page is null,
    // but narrow here so the rest can dereference page safely (and any future
    // caller gets a clear error instead of a cryptic null deref).
    throw new Error("No page found for this route");
  }
  const [websiteJsonRaw, appBarRaw, footerRaw] = await Promise.all([
    parseJsonNodeFromCompressed(bootstrap.page.websiteJson),
    parseJsonNodeFromCompressed(bootstrap.websiteConfig.appBar),
    parseJsonNodeFromCompressed(bootstrap.websiteConfig.footer),
  ]);

  const websiteJson = websiteJsonRaw as CraftData;
  const unbundledAppBar = unbundleSharedSection(appBarRaw, websiteJson);
  const unbundledFooter = unbundleSharedSection(footerRaw, websiteJson);

  const normalizedAppBar = normalizeSharedSectionNodeForPage(
    unbundledAppBar,
    websiteJson,
  );
  const normalizedFooter = normalizeSharedSectionNodeForPage(
    unbundledFooter,
    websiteJson,
  );

  const pageSlug = bootstrap.page.slug;
  const isAuthRoute =
    routeSlug === "/register" ||
    routeSlug === "/login" ||
    pageSlug === "/register" ||
    pageSlug === "/login" ||
    pageSlug === "login" ||
    pageSlug === "register";

  const appBarNode = isAuthRoute
    ? setStrippedFlag(normalizedAppBar)
    : normalizedAppBar;
  const footerNode = isAuthRoute
    ? setStrippedFlag(normalizedFooter)
    : normalizedFooter;

  const { nodeId: appbarId } = findComponentByType(
    websiteJson,
    "AppBarSection",
  );
  const appbarFallback = appbarId
    ? ""
    : findComponentByType(websiteJson, "AppBar").nodeId;
  const resolvedAppbarId = appbarId || appbarFallback;

  const { nodeId: footerId } = findComponentByType(
    websiteJson,
    "FooterSection",
  );
  const footerFallback = footerId
    ? ""
    : findComponentByType(websiteJson, "Footer").nodeId;
  const resolvedFooterId = footerId || footerFallback;

  if (resolvedAppbarId) {
    websiteJson[resolvedAppbarId] = appBarNode as CraftNodeValue;
  }
  if (resolvedFooterId) {
    websiteJson[resolvedFooterId] = footerNode as CraftNodeValue;
  }

  normalizeDataInputChildVisibility(websiteJson);
  normalizeRegisterSuccessVisibility(websiteJson);
  // Repair editor↔runtime prop-name drift in already-saved pages. Walks on
  // resolvedName rather than riding another pass's traversal, so it keeps
  // working wherever an aliased component is mounted.
  applyLegacyPropAliases(websiteJson as Record<string, unknown>);
  injectRuleBlockedProps(routeSlug, bootstrap, websiteJson);
  const safeJson = ensureKnownComponents(websiteJson, knownComponents);
  return JSON.stringify(safeJson);
}


function isSlugMatch(routeSlug: string, pageSlug: string | undefined, target: string): boolean {
  return routeSlug === `/${target}` || pageSlug === `/${target}` || pageSlug === target;
}

function isOutsideSchedule(availableFrom?: string, availableUntil?: string): boolean {
  if (!availableFrom && !availableUntil) return false;
  const now = new Date();
  if (availableFrom) {
    const from = new Date(availableFrom);
    if (now < from) return true;
  }
  if (availableUntil) {
    const until = new Date(availableUntil);
    if (now > until) return true;
  }
  return false;
}

function injectRuleBlockedProps(
  routeSlug: string,
  bootstrap: RuntimeBootstrapResponse,
  websiteJson: CraftData,
): void {
  // Called only after buildRenderablePageJson's null-page guard, so page is
  // present here; `?? ""` keeps the type-checker happy without a redundant throw.
  const pageSlug = bootstrap.page?.slug ?? "";

  // Inject registration blocked props
  if (bootstrap.registrationMode === "BLOCKED" && isSlugMatch(routeSlug, pageSlug, "register")) {
    const { nodeId } = findComponentByType(websiteJson, "RegisterChild");
    if (nodeId) {
      const node = websiteJson[nodeId];
      if (node && typeof node === "object") {
        node.props = {
          ...(node.props ?? {}),
          ruleRegistrationBlocked: true,
          registrationBlockedMessage: bootstrap.registrationBlockedMessage ?? "",
        };
      }
    }
  }

  // Inject login blocked props
  //
  // M35: a manual lockdown carrying an EXEMPTION rule is not a lockdown for
  // everyone. The worker evaluates `loginBlockConditions` at /auth/login and
  // lets matching guests through, but the conditions are deliberately kept
  // out of the anonymous bootstrap — so the runtime used to render the
  // blocked card for every visitor and the exempt guest had no form to reach
  // the endpoint that would have accepted them. A full lockdown in the UI,
  // in front of a partial one in the API.
  //
  // The bootstrap now carries one derived bit, `loginBlockHasExemptions`.
  // When it is set the card is suppressed and the form renders: the worker
  // stays the authority and refuses non-exempt guests at /auth/login with the
  // block message, which is the honest division — the client cannot know who
  // is exempt, because at bootstrap time nobody has identified themselves yet.
  //
  // The SCHEDULE window used to be absolute — "out of window ⇒ nobody gets
  // in" — and this mirrored that by blocking unconditionally. Segment
  // windows broke that assumption: a site outside its own window can still
  // admit the segment whose window is open, and the worker resolves that per
  // guest at `/auth/login`. Rendering the blocked card anyway would put a
  // full lockdown in the UI in front of a partial one in the API, and the
  // early-access guest would never reach the form that would accept them.
  // `scheduleHasSegmentWindows` is the same shape as the exemption bit: it
  // says only THAT a per-guest rule exists, never who it is for.
  const outsideSchedule =
    isOutsideSchedule(
      bootstrap.websiteConfig.availableFrom,
      bootstrap.websiteConfig.availableUntil,
    ) && bootstrap.websiteConfig.scheduleHasSegmentWindows !== true;
  const manualLockdown = bootstrap.websiteConfig.loginBlockEnabled === true;
  const hasExemptions = bootstrap.websiteConfig.loginBlockHasExemptions === true;
  const loginBlocked = outsideSchedule || (manualLockdown && !hasExemptions);
  if (loginBlocked && isSlugMatch(routeSlug, pageSlug, "login")) {
    const { nodeId } = findComponentByType(websiteJson, "LoginChild");
    if (nodeId) {
      const node = websiteJson[nodeId];
      if (node && typeof node === "object") {
        node.props = {
          ...(node.props ?? {}),
          ruleLoginBlocked: true,
          loginBlockedMessage: bootstrap.websiteConfig.loginBlockMessage ?? undefined,
        };
      }
    }
  }
}

/**
 * The builder's DataInput sets `isVisible: false` on linked child nodes
 * (FieldsGroup, CompanionsGroup, Checkboxes, Button, etc.) for steps that
 * aren't active at publish time. At runtime the engine's `nodeVisibilityFn`
 * blocks rendering of any node with `isVisible === false` *before* the
 * component code runs, so the components never get a chance to ignore the
 * flag. DataInput's own `display:none` wrappers handle step visibility
 * correctly, so we strip `isVisible` from all DataInput linked nodes here.
 */
/**
 * Audit 2026-09-23 (4b): the cockpit's Register editor writes
 * `isVisible = isSuccessSelected` onto the success group on every preview
 * switch and seeds it `false`; there is no operator control for it
 * (`RegisterSuccessGroupSettings` has none). The engine treats stored props as
 * the truth, so on the published page the stored `false` hid the card after
 * every successful registration. Strip it from that ONE node — resolved
 * through `linkedNodes.successGroup`, because legacy Craft ids do not follow
 * the `linked-<parent>-successGroup` shape — and leave its children alone:
 * their `isVisible` IS an operator toggle.
 */
function normalizeRegisterSuccessVisibility(websiteJson: CraftData): void {
  for (const node of Object.values(websiteJson)) {
    if (typeof node === "string" || !node) continue;
    if (node.type?.resolvedName !== "RegisterChild") continue;
    const successGroupId = node.linkedNodes?.successGroup;
    if (typeof successGroupId !== "string") continue;
    const successGroup = websiteJson[successGroupId];
    if (!successGroup || typeof successGroup === "string") continue;
    if (successGroup.props && typeof successGroup.props === "object") {
      delete (successGroup.props as Record<string, unknown>).isVisible;
    }
  }
}

function normalizeDataInputChildVisibility(websiteJson: CraftData): void {
  for (const node of Object.values(websiteJson)) {
    if (typeof node === "string" || !node) continue;
    if (node.type?.resolvedName !== "DataInput") continue;
    const linkedNodes = node.linkedNodes;
    if (!linkedNodes || typeof linkedNodes !== "object") continue;
    for (const linkedNodeId of Object.values(
      linkedNodes as Record<string, string>,
    )) {
      if (typeof linkedNodeId !== "string") continue;
      const linkedNode = websiteJson[linkedNodeId];
      if (!linkedNode || typeof linkedNode === "string") continue;
      if (linkedNode.props && typeof linkedNode.props === "object") {
        delete (linkedNode.props as Record<string, unknown>).isVisible;
      }
    }
  }
}
